import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Search, Upload } from "lucide-react";
import { MaterialAudienceFilter } from "../../features/materials/slice-extensions/materials-slice-extension";
import { Group } from "../../features/members/models/Group";
import { Member } from "../../features/members/models/Member";

interface MaterialsPageHeaderProps {
  categories: string[];
  activeCategory: string | null;
  search: string;
  audience: MaterialAudienceFilter;
  members: Member[];
  groups: Group[];
  uploading: boolean;
  onCategoryChange: (category: string | null) => void;
  onSearchChange: (search: string) => void;
  onAudienceChange: (audience: MaterialAudienceFilter) => void;
  onFilePicked: (file: File) => void;
}

/** Title, intro, the upload action and the category filter chips. */
const MaterialsPageHeader: React.FC<MaterialsPageHeaderProps> = ({
  categories,
  activeCategory,
  search,
  audience,
  members,
  groups,
  uploading,
  onCategoryChange,
  onSearchChange,
  onAudienceChange,
  onFilePicked,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so re-picking the same file fires the change event again.
    event.target.value = "";
    if (file) {
      onFilePicked(file);
    }
  };

  const filterOptions: (string | null)[] = [null, ...categories];

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="mb-1 text-2xl font-bold text-grey-20 dark:text-white">
            {t("materials.title", "Materials")}
          </h1>
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {t("materials.pageIntro", "Files shared with everyone in your organization — sheet music, notes, documents.")}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <Upload size={16} strokeWidth={1.5} />
          {uploading
            ? t("materials.uploading", "Uploading…")
            : t("materials.uploadAction", "Upload file")}
        </button>
        <input type="file" className="hidden" ref={fileInputRef} onChange={handleFilePicked} />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {filterOptions.map((option) => (
          <button
            key={option ?? "all"}
            type="button"
            className={option === activeCategory ? "btn btn-primary" : "btn btn-secondary"}
            onClick={() => onCategoryChange(option)}
          >
            {option ?? t("materials.allCategories", "All")}
          </button>
        ))}
        <select
          className="input ml-auto w-full py-2 sm:w-56"
          aria-label={t("materials.audienceFilterLabel", "Shared with")}
          value={audience.kind === "all" ? "all" : `${audience.kind}:${audience.id ?? ""}`}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "all") return onAudienceChange({ kind: "all" });
            if (value === "everyone") return onAudienceChange({ kind: "everyone" });
            const [kind, id] = value.split(":");
            onAudienceChange({ kind: kind as "group" | "member", id });
          }}
        >
          <option value="all">{t("materials.audienceFilterAll", "Shared with: anyone")}</option>
          <option value="everyone">{t("materials.audienceFilterEveryone", "Everyone only")}</option>
          {groups.length > 0 && (
            <optgroup label={t("materials.audienceGroups", "Groups")}>
              {groups.map((group) => (
                <option key={group.id} value={`group:${group.id}`}>{group.name}</option>
              ))}
            </optgroup>
          )}
          <optgroup label={t("materials.audienceFilterVisibleTo", "Visible to member")}>
            {members.map((member) => (
              <option key={member.id} value={`member:${member.id}`}>
                {member.firstName} {member.lastName}
              </option>
            ))}
          </optgroup>
        </select>
        <div className="relative w-full sm:w-64">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-grey-50 dark:text-grey-60"
          />
          <input
            type="search"
            className="input py-2 pl-9"
            value={search}
            placeholder={t("materials.searchPlaceholder", "Search files…")}
            aria-label={t("materials.searchLabel", "Search materials")}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>
    </>
  );
};

export default MaterialsPageHeader;
